<template>
  <div  :class="fullView ? 'full-view' : 'claims-value-contractor-echarts'">
    <v-chart class="chart" :option="option" ref="chartRef" :init-options="{ renderer: 'svg' }"/>
  </div>
</template>

<script>
import { BarChart } from 'echarts/charts'
import { SVGRenderer } from 'echarts/renderers'
import { TooltipComponent, GridComponent } from 'echarts/components'
import { use } from 'echarts/core'
import { useChartResize } from '@/composables/useChartResize'
import VChart from 'vue-echarts'
import { COLOR_PALETTE, getDefaultChartTextStyle, getBarNoDataOptions } from '@/helpers'
import { emitChartFinished } from '@/utils/insightsPdfUtils'

use([TooltipComponent, GridComponent, BarChart, SVGRenderer])

export default {
  name: 'VariationsTimeImpactPerRootCausesChart',
  components: {
    VChart
  },
  props: {
    resultSet: {
      type: Object,
      required: true,
      default: () => ({})
    },
    fullView: {
      type: Boolean
    }
  },
  setup () {
    const { chartRef, handleResize } = useChartResize()

    return {
      chartRef,
      handleResize
    }
  },
  data () {
    return {
      option: {
        animation: !this.fullView,
        color: COLOR_PALETTE,
        title: {
          text: this.$t('insights.charts.variationsTimeImpactPerRootCauses.title'),
          left: 'center',
          top: 0,
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function (params) {
            return '<b>' + params[0].name + '</b><br/>' +
                  params[0].seriesName + ': ' + params[0].value
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: [],
            axisTick: {
              alignWithLabel: true
            },
            axisLabel: {
              interval: 0,
              rotate: 90,
              fontSize: 10,
              show: this.fullView
            }
          }
        ],
        yAxis: [
          {
            type: 'value'
          }
        ],
        series: [
          {
            name: this.$t('insights.charts.variationsTimeImpactPerRootCauses.seriesName'),
            type: 'bar',
            barWidth: '60%',
            data: []
          }
        ]
      }
    }
  },
  methods: {
    updateData () {
      const noDataOptions = getBarNoDataOptions(this.resultSet, this.fullView, this.option)
      if (noDataOptions) {
        this.option = {
          ...this.option,
          ...noDataOptions,
          title: this.option.title
        }
        emitChartFinished(this, true)
        return
      }

      const pivot = this.resultSet.chartPivot()
      const labels = pivot.map((r) => r.x)
      const data = pivot.map((r) => r['variations_parts.eotagreed'])

      const combinedData = labels.map((label, index) => ({
        label,
        value: data[index]
      }))
        .filter(item => item.label !== '[Empty string]')
        .sort((a, b) => b.value - a.value)

      const sortedLabels = combinedData.map(item => item.label)
      const sortedData = combinedData.map(item => item.value)

      this.option.xAxis[0].data = sortedLabels
      this.option.series[0].data = sortedData.map(value => ({
        value,
        itemStyle: {
          color: COLOR_PALETTE[0],
          borderWidth: 1
        }
      }))
      emitChartFinished(this, false)
    }
  },
  mounted () {
    this.updateData()
  }
}
</script>

<style scoped>
.claims-value-contractor-echarts {
  height: 250px;
}
.full-view {
  height: var(--full-view-chart-height);
  width: var(--full-view-chart-width);
}
</style>
